import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, Image as ImageIcon, X } from 'lucide-react';
import { apiUrl, adminFetch } from '../../api';

const MAX_IMAGES = 5;

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const emptyListing = { title: '', description: '', date: '', time: '', location: '', link: '', tags: '', images: [] };

function AdminCollections() {
  const [collections, setCollections] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [listings, setListings] = useState([]);
  const [newCollection, setNewCollection] = useState({ name: '', description: '' });
  const [listingForm, setListingForm] = useState(emptyListing);
  const [showListingForm, setShowListingForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadCollections = useCallback(async () => {
    try {
      const res = await adminFetch(apiUrl('/api/admin/collections'));
      const data = await res.json();
      if (data.success) {
        setCollections(data.collections);
        setActiveId((prev) => prev || (data.collections[0] && data.collections[0]._id) || null);
      }
    } catch {
      setError('Could not load collections. Is the server running?');
    }
  }, []);

  const loadListings = useCallback(async (id) => {
    if (!id) { setListings([]); return; }
    try {
      const res = await adminFetch(apiUrl(`/api/admin/collections/${id}/listings`));
      const data = await res.json();
      if (data.success) setListings(data.listings);
    } catch {
      setError('Could not load listings.');
    }
  }, []);

  useEffect(() => { loadCollections(); }, [loadCollections]);
  useEffect(() => { loadListings(activeId); }, [activeId, loadListings]);

  const addCollection = async (e) => {
    e.preventDefault();
    if (!newCollection.name.trim()) return;
    setBusy(true); setError('');
    try {
      const res = await adminFetch(apiUrl('/api/admin/collections'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newCollection),
      });
      const data = await res.json();
      if (!data.success) { setError(data.message); return; }
      setNewCollection({ name: '', description: '' });
      await loadCollections();
      setActiveId(data.collection._id);
    } finally { setBusy(false); }
  };

  const deleteCollection = async (id) => {
    if (!window.confirm('Delete this collection and all its listings?')) return;
    await adminFetch(apiUrl(`/api/admin/collections/${id}`), { method: 'DELETE' });
    if (activeId === id) setActiveId(null);
    await loadCollections();
  };

  const onPickImages = async (e) => {
    const files = Array.from(e.target.files || []);
    const room = MAX_IMAGES - listingForm.images.length;
    if (room <= 0) { setError(`You can upload at most ${MAX_IMAGES} images.`); return; }
    const encoded = await Promise.all(files.slice(0, room).map(fileToBase64));
    setListingForm((f) => ({ ...f, images: [...f.images, ...encoded] }));
    e.target.value = '';
  };

  const removeImage = (idx) =>
    setListingForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const addListing = async (e) => {
    e.preventDefault();
    const tags = listingForm.tags.split(',').map((t) => t.trim()).filter(Boolean);
    // Require every field before posting
    if (!activeId) { setError('Select a collection first.'); return; }
    if (!listingForm.title.trim()) { setError('Title is required.'); return; }
    if (!listingForm.description.trim()) { setError('Description is required.'); return; }
    if (!listingForm.date.trim()) { setError('Date is required.'); return; }
    if (!listingForm.time.trim()) { setError('Time is required.'); return; }
    if (!listingForm.location.trim()) { setError('Location is required.'); return; }
    if (tags.length === 0) { setError('Add at least one tag.'); return; }
    if (listingForm.images.length === 0) { setError('Add at least one photo.'); return; }
    setBusy(true); setError('');
    try {
      const payload = {
        collectionId: activeId,
        title: listingForm.title,
        description: listingForm.description,
        date: listingForm.date,
        time: listingForm.time,
        location: listingForm.location,
        link: listingForm.link,
        tags,
        images: listingForm.images,
      };
      const res = await adminFetch(apiUrl('/api/admin/listings'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) { setError(data.message); return; }
      setListingForm(emptyListing);
      setShowListingForm(false);
      await loadListings(activeId);
      await loadCollections();
    } finally { setBusy(false); }
  };

  const deleteListing = async (id) => {
    if (!window.confirm('Delete this listing?')) return;
    await adminFetch(apiUrl(`/api/admin/listings/${id}`), { method: 'DELETE' });
    await loadListings(activeId);
    await loadCollections();
  };

  const activeCollection = collections.find((c) => c._id === activeId);

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <form className="admin-add-collection" onSubmit={addCollection}>
          <input
            className="admin-input sm"
            placeholder="New collection name"
            value={newCollection.name}
            onChange={(e) => setNewCollection({ ...newCollection, name: e.target.value })}
          />
          <input
            className="admin-input sm"
            placeholder="Description (optional)"
            value={newCollection.description}
            onChange={(e) => setNewCollection({ ...newCollection, description: e.target.value })}
          />
          <button className="admin-btn-primary sm" type="submit" disabled={busy}>
            <Plus size={15} /> Add Collection
          </button>
        </form>

        <div className="admin-collection-list">
          {collections.length === 0 && <p className="admin-muted">No collections yet.</p>}
          {collections.map((c) => (
            <div
              key={c._id}
              className={`admin-collection-item ${activeId === c._id ? 'active' : ''}`}
              onClick={() => setActiveId(c._id)}
            >
              <div className="admin-collection-meta">
                <span className="admin-collection-name">{c.name}</span>
                <span className="admin-collection-count">{c.listingCount} listing{c.listingCount === 1 ? '' : 's'}</span>
              </div>
              <button
                className="admin-icon-btn"
                title="Delete collection"
                onClick={(e) => { e.stopPropagation(); deleteCollection(c._id); }}
              >
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>
      </aside>

      <main className="admin-main">
        <header className="admin-main-header">
          <div>
            <h2>{activeCollection ? activeCollection.name : 'Collections'}</h2>
            <p className="admin-muted">
              {activeCollection ? (activeCollection.description || 'Manage listings in this collection') : 'Select or create a collection to begin'}
            </p>
          </div>
          {activeCollection && (
            <button className="admin-btn-primary" onClick={() => setShowListingForm((s) => !s)}>
              {showListingForm ? <><X size={16} /> Close</> : <><Plus size={16} /> Add Listing</>}
            </button>
          )}
        </header>

        {error && <div className="admin-error">{error}</div>}

        {activeCollection && showListingForm && (
          <form className="admin-listing-form" onSubmit={addListing}>
            <div className="admin-form-grid">
              <div className="admin-field span2">
                <label className="admin-label">Title *</label>
                <input className="admin-input" value={listingForm.title}
                  onChange={(e) => setListingForm({ ...listingForm, title: e.target.value })} />
              </div>
              <div className="admin-field span2">
                <label className="admin-label">Description *</label>
                <textarea className="admin-input" rows={3} value={listingForm.description}
                  onChange={(e) => setListingForm({ ...listingForm, description: e.target.value })} />
              </div>
              <div className="admin-field">
                <label className="admin-label">Date *</label>
                <input className="admin-input" placeholder="April 12, 2026" value={listingForm.date}
                  onChange={(e) => setListingForm({ ...listingForm, date: e.target.value })} />
              </div>
              <div className="admin-field">
                <label className="admin-label">Time *</label>
                <input className="admin-input" placeholder="2:00 PM - 4:00 PM" value={listingForm.time}
                  onChange={(e) => setListingForm({ ...listingForm, time: e.target.value })} />
              </div>
              <div className="admin-field">
                <label className="admin-label">Location *</label>
                <input className="admin-input" placeholder="Glasgow" value={listingForm.location}
                  onChange={(e) => setListingForm({ ...listingForm, location: e.target.value })} />
              </div>
              <div className="admin-field">
                <label className="admin-label">Tags (comma separated) *</label>
                <input className="admin-input" placeholder="Workshop, Chai" value={listingForm.tags}
                  onChange={(e) => setListingForm({ ...listingForm, tags: e.target.value })} />
              </div>
              <div className="admin-field span2">
                <label className="admin-label">Join / RSVP link (URL)</label>
                <input className="admin-input" placeholder="https://… (booking, WhatsApp group, sign-up)" value={listingForm.link}
                  onChange={(e) => setListingForm({ ...listingForm, link: e.target.value })} />
              </div>
            </div>

            <div className="admin-field">
              <label className="admin-label">Photos ({listingForm.images.length}/{MAX_IMAGES}) *</label>
              <div className="admin-image-row">
                {listingForm.images.map((src, i) => (
                  <div className="admin-thumb" key={i}>
                    <img src={src} alt={`upload ${i + 1}`} />
                    <button type="button" className="admin-thumb-x" onClick={() => removeImage(i)}><X size={12} /></button>
                  </div>
                ))}
                {listingForm.images.length < MAX_IMAGES && (
                  <label className="admin-upload-box">
                    <ImageIcon size={18} />
                    <span>Add</span>
                    <input type="file" accept="image/*" multiple hidden onChange={onPickImages} />
                  </label>
                )}
              </div>
            </div>

            <button className="admin-btn-primary" type="submit" disabled={busy}>
              {busy ? 'Saving…' : 'Save Listing'}
            </button>
          </form>
        )}

        {activeCollection && (
          <div className="admin-listing-grid">
            {listings.length === 0 && <p className="admin-muted">No listings yet. Add one above.</p>}
            {listings.map((l) => (
              <div className="admin-listing-card" key={l._id}>
                <div className="admin-listing-thumb">
                  {l.images && l.images[0]
                    ? <img src={l.images[0]} alt={l.title} />
                    : <div className="admin-listing-noimg"><ImageIcon size={22} /></div>}
                  {l.images && l.images.length > 1 && (
                    <span className="admin-listing-count">+{l.images.length - 1}</span>
                  )}
                </div>
                <div className="admin-listing-body">
                  <h4>{l.title}</h4>
                  {(l.date || l.location) && (
                    <p className="admin-muted sm">{[l.date, l.location].filter(Boolean).join(' · ')}</p>
                  )}
                  {l.description && <p className="admin-listing-desc">{l.description}</p>}
                  {l.tags && l.tags.length > 0 && (
                    <div className="admin-tag-row">{l.tags.map((t) => <span key={t} className="admin-tag">{t}</span>)}</div>
                  )}
                </div>
                <button className="admin-icon-btn danger" onClick={() => deleteListing(l._id)} title="Delete listing">
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default AdminCollections;
